import React, { useContext, useEffect, useMemo, useState } from 'react';
import { TranslationKey } from './../../../../../types/translation/TranslationKey';
import { Grid } from '@mui/material';
import TextInput from '../../../Common/TextInput';
import { t, translate } from '../../../../../types/translation/Translator';
import DropdownSelect from '../../../Common/DropdownSelect';
import { countries } from '../../../../../util/country';
import Checkbox from '../../../../../VentoryUI/components/common/Checkbox/Checkbox';
import Dropdown from '../../../Common/Dropdown';
import { UnitSystem, UnitType } from '../../../../../types/uom';
import { testIds } from '../../../../../util/identifiers/identifiers.util';
import Paper from '../../../../../VentoryUI/components/common/Paper';
import FlexPane from '../../../../../VentoryUI/components/common/FlexPane';
import { FileContext } from '../../../../../context/FileContext';
import CustomFieldInput from '../../../Common/CustomFieldInput';
import { CustomFieldContext } from '../../../../../context/CustomFieldContext';
import { CustomFieldEntityType, WithCustomFields } from '../../../../../types/custom_field/customField';
import { ImagePicker } from '../../../../../VentoryUI/components/common/File/Image';
import { FilePreview, FileEntityType } from '../../../../../types/file';
import { CompanyRoleAssignmentContext } from '../../../../../context/CompanyRoleAssignmentContext';
import { TagRelationAuthorization } from '../../../../../types/tag/tagRelation';
import { CompanyContext } from '../../../../../context/CompanyContext';
import { UnitOfMeasureUtil } from '../../../../../types/uom';
import { UserContext } from '../../../../../context/UserContext';
import { ContactContext } from '../../../../../context/ContactContext';
import { ProductMasterDataContext } from '../../../../../context/ProductMasterDataContext';
import { Unilever } from '../../../../../types/customer/DHL/Unilever/Unilever';
import { ProductMasterDataInput } from '../../../../../types/product_master_data';

interface ProductMasterDataInfoPaneInputProps {
  filesInput?: File[] | null;
  footer: React.ReactNode;

  productMasterDataInput: ProductMasterDataInput;
  setProductMasterDataInput: (pmd: ProductMasterDataInput) => void;

  title?: string;
  uploadFilesComponent?: React.ReactNode;
  tagInput?: React.ReactNode;
}

export default function ProductMasterDataInfoPane({
  footer,

  productMasterDataInput,
  setProductMasterDataInput,

  title,
  filesInput,
  uploadFilesComponent,
  tagInput,
}: ProductMasterDataInfoPaneInputProps) {
  const { currentCompany } = useContext(CompanyContext);
  const { customFields } = useContext(CustomFieldContext);
  const { files } = useContext(FileContext);
  const { hasRole, isAdmin } = useContext(CompanyRoleAssignmentContext);
  const { companyUsers } = useContext(UserContext);
  const { contacts } = useContext(ContactContext);
  const { productMasterData } = useContext(ProductMasterDataContext);

  const [displayImages, setDisplayImages] = useState<(string | FilePreview)[]>([]);

  const cf = useMemo(() => {
    return [...customFields.values()]
      .filter(cf => cf.entityType === CustomFieldEntityType.productMasterData && !cf.hidden)
      .sort((c1, c2) => c1.name.localeCompare(c2.name));
  }, [customFields]);

  const handleDisplayImages = async () => {
    const existingImageIds = [...files.values()]
      .filter(f => f.fileTypeCategory === 'image' && f.entityId === productMasterDataInput.id)
      .map(f => f.id);

    if (!filesInput?.length) return setDisplayImages([...existingImageIds]);

    // const previewImages: FilePreview[] = await Promise.all(
    //   filesInput
    //     .filter(file => FileUtils.getFileTypeCategory(file) === 'image')
    //     .map(async file => {
    //       return {
    //         file: file,
    //         name: file.name,
    //         size: FileUtils.fileSizeToFormattedString(file.size, 1),
    //         type: file.type,
    //         data: await FileUtils.fileToBase64(file, { prefix: true }),
    //       };
    //     }),
    // );

    // setDisplayImages([...existingImageIds, ...previewImages]);
  };

  useEffect(() => {
    handleDisplayImages();
  }, [files, filesInput, productMasterDataInput]);

  const hasExpiryManagement = currentCompany.settings.featureToggles.lots.expiryManagement;

  const isExternal = (cf: any) => {
    if (Unilever.companyIds.includes(cf.companyId)) return false;
    return !!cf.externalIdentifier;
  };

  const computeUnitTypeOptions = () => {
    if (productMasterDataInput.kitManaged) return [UnitType.quantity];
    return Object.values(UnitType);
  };

  return (
    <FlexPane
      testId={testIds.productMasterDataInfoPane}
      content={
        <Paper>
          <Grid container rowSpacing={2} columnSpacing={2}>
            {title ? (
              <Grid item xs={12}>
                <p className='text-xl font-semibold'>{title}</p>
              </Grid>
            ) : null}
            {/* Column 1 */}
            <Grid item xs={4}>
              <Grid container rowSpacing={1}>
                <Grid item xs={12}>
                  <TextInput
                    mandatory
                    disabled={!!productMasterDataInput.externalIdentifier || !isAdmin}
                    value={productMasterDataInput.productNumber}
                    label={t(TranslationKey.productNumber).singular.label}
                    placeholder={t(TranslationKey.productNumber).singular.label}
                    onChange={v => setProductMasterDataInput(productMasterDataInput.withProductNumber(v))}
                    testId={testIds.productNumber}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextInput
                    mandatory
                    disabled={!!productMasterDataInput.externalIdentifier || !isAdmin}
                    value={productMasterDataInput.productName}
                    label={t(TranslationKey.productName).singular.label}
                    placeholder={t(TranslationKey.productName).singular.label}
                    onChange={v => setProductMasterDataInput(productMasterDataInput.withProductName(v))}
                    testId={testIds.productName}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextInput
                    disabled={!!productMasterDataInput.externalIdentifier || !isAdmin}
                    label={t(TranslationKey.globalTradeItemNumber).singular.label.toUpperCase()}
                    placeholder={t(TranslationKey.globalTradeItemNumber).singular.label.toUpperCase()}
                    onChange={v => setProductMasterDataInput(productMasterDataInput.withEAN(v))}
                    value={productMasterDataInput.globalTradeItemNumber || ''}
                  />
                </Grid>
                <Grid item xs={12}>
                  <DropdownSelect
                    placeholder={t(TranslationKey.countryOfOrigin).singular.label}
                    label={t(TranslationKey.countryOfOrigin).singular.label}
                    selectedValue={countries.find(c => c.cca2 === productMasterDataInput.countryOfOrigin) || null}
                    maxHeight='150px'
                    disabled={!!productMasterDataInput.externalIdentifier || !isAdmin}
                    values={countries}
                    toText={(item: any) => item.name.common}
                    onChange={(item: any) =>
                      setProductMasterDataInput(
                        productMasterDataInput.withCountryOfOrigin(item ? item.cca2 : undefined),
                      )
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <div className='my-2 grid gap-1'>
                    <Checkbox
                      disabled={!!productMasterDataInput.externalIdentifier || !isAdmin}
                      label={t(TranslationKey.serial).singular.label}
                      onChange={v => setProductMasterDataInput(productMasterDataInput.withSerialManaged(v))}
                      value={productMasterDataInput.serialManaged || false}
                      testId={testIds.serialManaged}
                    />
                    <Checkbox
                      disabled={!!productMasterDataInput.externalIdentifier || !isAdmin}
                      label={t(TranslationKey.kit).singular.label}
                      onChange={v => setProductMasterDataInput(productMasterDataInput.withKitManaged(v))}
                      value={productMasterDataInput.kitManaged || false}
                    />
                    <Checkbox
                      disabled={!!productMasterDataInput.externalIdentifier || !isAdmin}
                      label={t(TranslationKey.handlingUnit).singular.label}
                      onChange={v => setProductMasterDataInput(productMasterDataInput.withHandlingUnit(v))}
                      value={productMasterDataInput.handlingUnit || false}
                    />
                    <Checkbox
                      disabled={!!productMasterDataInput.externalIdentifier || !isAdmin}
                      label={t(TranslationKey.lot).singular.label}
                      onChange={v => setProductMasterDataInput(productMasterDataInput.withLotManaged(v))}
                      value={productMasterDataInput.lotManaged || false}
                      testId={testIds.lotManaged}
                    />
                    {productMasterDataInput.lotManaged && hasExpiryManagement ? (
                      <Checkbox
                        disabled={!!productMasterDataInput.externalIdentifier || !isAdmin}
                        label={t(TranslationKey.expiryTracking).singular.upper}
                        onChange={v => setProductMasterDataInput(productMasterDataInput.withExpiryTracking(v))}
                        value={productMasterDataInput.expiryTracking || false}
                        tooltip={t(TranslationKey.expiryTrackingTooltip).singular.upper}
                      />
                    ) : null}
                  </div>
                </Grid>
                <Grid item xs={12}>
                  <Grid container>
                    <Grid item xs={12}>
                      <Dropdown<UnitType>
                        disabled={!!productMasterDataInput.externalIdentifier || !isAdmin}
                        testId={testIds.unitType}
                        label={t(TranslationKey.unitOfMeasure).singular.label}
                        values={computeUnitTypeOptions()}
                        selectedValue={productMasterDataInput.unitOfMeasure?.unitType || UnitType.quantity}
                        toText={item => UnitOfMeasureUtil.unitTypeToString(item)}
                        onChange={item => setProductMasterDataInput(productMasterDataInput.withUnitType(item))}
                      />
                    </Grid>
                    {productMasterDataInput.unitOfMeasure?.unitType &&
                    !UnitOfMeasureUtil.needsUnit(productMasterDataInput.unitOfMeasure?.unitType) ? (
                      <>
                        <Grid item xs={12}>
                          <Dropdown<UnitSystem>
                            disabled={!!productMasterDataInput.externalIdentifier}
                            testId={testIds.unitSystem}
                            values={Array.from(Object.values(UnitSystem))}
                            selectedValue={productMasterDataInput.unitOfMeasure?.system || UnitSystem.metric}
                            toText={item => translate({ key: item as TranslationKey })}
                            onChange={item => setProductMasterDataInput(productMasterDataInput.withUnitSystem(item))}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Dropdown
                            disabled={!!productMasterDataInput.externalIdentifier}
                            testId={testIds.unit}
                            maxHeight='200px'
                            values={UnitOfMeasureUtil.unitValues(productMasterDataInput.unitOfMeasure)}
                            selectedValue={
                              productMasterDataInput.unitOfMeasure?.unit ||
                              UnitOfMeasureUtil.defaultUnit(productMasterDataInput.unitOfMeasure)
                            }
                            toText={item => UnitOfMeasureUtil.unitText(item, productMasterDataInput.unitOfMeasure)}
                            onChange={item => setProductMasterDataInput(productMasterDataInput.withUnit(item))}
                          />
                        </Grid>
                      </>
                    ) : null}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid xs={4} item>
              {isAdmin ? (
                <>
                  <p className='mb-1 text-[14px] text-sm font-medium text-slate-800'>
                    {t(TranslationKey.productImage).singular.label}
                  </p>
                  <ImagePicker
                    fileEntityType={FileEntityType.pmd}
                    width='100%'
                    onRemove={() => setProductMasterDataInput(productMasterDataInput.withImage(undefined))}
                    fileId={productMasterDataInput.image || ''}
                    onSelected={selected => setProductMasterDataInput(productMasterDataInput.withImage(selected))}
                    fileSasIdsOrPreviews={displayImages}
                  />
                </>
              ) : null}
              <Grid mt={1}>{uploadFilesComponent || null}</Grid>
            </Grid>
            <Grid item xs={4}>
              <div className='grid max-h-[720px] gap-4 overflow-auto pr-3 align-top'>
                {hasRole(new TagRelationAuthorization().forCreate()) && tagInput}
                {cf.length ? (
                  <div className='gap-1'>
                    {cf.map(cf => (
                      <Grid item mb={2} key={`custom_field_input${cf.id}`}>
                        <CustomFieldInput
                          disabled={isExternal(cf) || cf.generated || !isAdmin}
                          value={WithCustomFields.toCustomFieldsMap(productMasterDataInput).get(cf.id)?.value}
                          entity={productMasterDataInput}
                          item={customFields.get(cf.id)}
                          onChange={setProductMasterDataInput}
                          users={companyUsers}
                          contacts={contacts}
                          productMasterData={productMasterData}
                        />
                      </Grid>
                    ))}
                  </div>
                ) : null}
              </div>
            </Grid>
          </Grid>
        </Paper>
      }
      footer={
        <Grid container>
          <Grid item xs={12} alignContent={'flex-end'}>
            {footer}
          </Grid>
        </Grid>
      }
    />
  );
}
